'use client';

import { BlockquoteSection } from '@/types/sanity';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { SectionCTAs } from './SectionCTAs';
import { ScrollReveal } from '@/components/animations/ScrollAnimations';
import { urlFor } from '@/sanity/lib/image';
import { cn } from '@/lib/utils';

interface BlockquoteSectionComponentProps {
  section: BlockquoteSection;
  pageSlug?: string;
}

export function BlockquoteSectionComponent({ section, pageSlug }: BlockquoteSectionComponentProps) {
  return (
    <ScrollReveal direction="up" delay={0.2}>
      <section
        className={cn(
          'blockquote-section py-8 md:py-12 lg:py-16 bg-muted/30',
          section.backgroundImage?.asset && 'bg-cover bg-center',
          pageSlug && `blockquote-section--${pageSlug}`
        )}
        style={{
          backgroundColor: section.backgroundColor,
          ...(section.backgroundImage?.asset && {
            backgroundImage: `url(${urlFor(section.backgroundImage).width(1920).quality(80).url()})`,
          }),
        }}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            {section.title && (
              <h2 className="text-center font-medium mb-8 text-section-title leading-section-title">
                {section.title}
              </h2>
            )}
            <blockquote className="max-w-3xl mx-auto text-center">
              <svg
                className="h-8 w-8 mx-auto mb-4 text-primary/30"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
              <p className="text-blockquote-quote leading-blockquote-quote italic text-foreground/80 mb-6">
                &ldquo;{section.quote}&rdquo;
              </p>
              <Separator className="my-6 max-w-xs mx-auto" />
              <footer className="flex items-center justify-center gap-3">
                {section.authorImage && (
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={
                        section.authorImage?.asset
                          ? urlFor(section.authorImage).width(96).height(96).url()
                          : undefined
                      }
                      alt={section.authorImage?.alt || section.author}
                    />
                    <AvatarFallback>{section.author.charAt(0)}</AvatarFallback>
                  </Avatar>
                )}
                <div className="text-left">
                  <cite className="not-italic font-semibold text-sm block">{section.author}</cite>
                  {section.authorRole && (
                    <p className="text-muted-foreground text-blockquote-role leading-blockquote-role">
                      {section.authorRole}
                    </p>
                  )}
                </div>
              </footer>
            </blockquote>

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
